import { z } from "zod";

/**
 * Connect flow configuration - "landers" (requirements.md section 10).
 *
 * One contact flow serves many numbers by asking the portal what to do.
 * The organising idea is that a phone number exists in exactly one
 * Connect instance, so keying configuration on a number or an ARN forces
 * a customer with three environments to author everything three times.
 * Instead:
 *
 *   profile  - authored once, environment-agnostic, refers to Connect
 *              resources by logical name and never by ARN
 *   inventory - what one environment actually has: logical name -> ARN
 *   binding   - which phone numbers reach a profile, per environment
 *
 * Resolution at call time is: dialled number -> profile (this
 * environment's bindings) -> logical references resolved to real ARNs
 * (this environment's inventory).
 */

/**
 * Everything is addressed as `(type, name)` - content and Connect
 * resources alike - so authoring, storage, the wire format and
 * resolution share one addressing model rather than three.
 *
 * Content types hold a literal value authored in the profile. Reference
 * types hold a logical name resolved to an ARN through the environment's
 * inventory. The caller never needs to know which is which.
 */
export const ContentFieldTypeSchema = z.enum(["announcement", "kv", "voice", "hours", "menu"]);
export type ContentFieldType = z.infer<typeof ContentFieldTypeSchema>;

export const ReferenceFieldTypeSchema = z.enum(["queue", "flow", "prompt", "hours_of_operation", "lex_bot"]);
export type ReferenceFieldType = z.infer<typeof ReferenceFieldTypeSchema>;

export const FieldTypeSchema = z.enum([...ContentFieldTypeSchema.options, ...ReferenceFieldTypeSchema.options]);
export type FieldType = z.infer<typeof FieldTypeSchema>;

export function isReferenceType(type: FieldType): type is ReferenceFieldType {
  return (ReferenceFieldTypeSchema.options as readonly string[]).includes(type);
}

/**
 * Snake case only. The name is read from a contact flow as
 * `$.External.announcements.hold_1`, and Connect's own guidance on
 * attribute values is unreliable enough that keeping keys to
 * alphanumerics and underscore removes the question.
 */
export const FieldNameSchema = z.string().regex(/^[a-z0-9][a-z0-9_]{0,63}$/);

/** `${type}/${name}` - the same address the audit log records a change against. */
export const FieldKeySchema = z.string().regex(/^[a-z_]+\/[a-z0-9][a-z0-9_]{0,63}$/);
export function fieldKey(type: FieldType, name: string): string {
  return `${type}/${name}`;
}
export function parseFieldKey(key: string): { type: FieldType; name: string } {
  const [type, name] = key.split("/", 2);
  return { type: FieldTypeSchema.parse(type), name: FieldNameSchema.parse(name) };
}

/**
 * Hours are authored as a schedule but never returned as one: the lander
 * evaluates them and returns `open: false` plus the announcement to
 * play. Business logic does not belong in a contact flow (section 10.3).
 */
export const HoursScheduleSchema = z.object({
  /** IANA zone. Evaluated in the customer's account at call time. */
  timezone: z.string().min(1).max(64),
  weekly: z
    .array(
      z.object({
        day: z.number().int().min(0).max(6),
        opens: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        closes: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
      }),
    )
    .max(21),
  holidays: z
    .array(
      z.object({
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        name: z.string().min(1).max(128),
        /** A holiday may open late rather than close: both are the same shape. */
        closed: z.boolean(),
        opens: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional(),
        closes: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional(),
        /**
         * The name of one of the profile's announcements (the owner's ask of 2026-09-18: one per holiday, the customer
         * in control). The lander resolves it to the payload at call time; a value that names no announcement plays as
         * it is, so a sentence typed here still works.
         */
        announcement: z.string().max(4000).optional(),
      }),
    )
    .max(80)
    .optional(),
  /** The announcement played outside the windows: a name of one of the profile's announcements, resolved by the lander, or literal text. */
  closed_announcement: z.string().max(4000).optional(),
});
export type HoursSchedule = z.infer<typeof HoursScheduleSchema>;

/** A menu is an array of objects because `JSON` response validation allows it (section 10.3). */
export const MenuOptionSchema = z.object({
  dtmf: z.string().regex(/^[0-9*#]$/),
  label: z.string().min(1).max(128),
  target: z.object({ type: ReferenceFieldTypeSchema, name: FieldNameSchema }),
});
export type MenuOption = z.infer<typeof MenuOptionSchema>;
export const MenuSchema = z.array(MenuOptionSchema).max(12);

const named = { name: FieldNameSchema };

/**
 * One authored field. Adding a type is data, not a deployment: a new
 * content type is a new key, a new reference type a new inventory
 * category (section 10.2).
 */
export const ProfileFieldSchema = z.discriminatedUnion("type", [
  z.object({ ...named, type: z.literal("announcement"), value: z.string().min(1).max(4000) }),
  z.object({ ...named, type: z.literal("kv"), value: z.string().max(1024) }),
  z.object({ ...named, type: z.literal("voice"), value: z.string().regex(/^[A-Za-z]{2,32}$/) }),
  z.object({ ...named, type: z.literal("hours"), value: HoursScheduleSchema }),
  z.object({ ...named, type: z.literal("menu"), value: MenuSchema }),
  ...ReferenceFieldTypeSchema.options.map((t) =>
    // A logical name ("Tier 1 Queue"), resolved through the inventory. Customers never see an ARN.
    z.object({ ...named, type: z.literal(t), ref: z.string().min(1).max(256) }),
  ),
]);
export type ProfileField = z.infer<typeof ProfileFieldSchema>;

export function fieldAddress(field: ProfileField): string {
  return fieldKey(field.type, field.name);
}

/** `Profiles` - PK `organisation_id`, SK `profile_id`. Environment-agnostic by design. */
export const ProfileRecordSchema = z.object({
  organisation_id: z.string().min(1),
  profile_id: z.string().min(1),
  /** What the customer calls it: "Global Helpdesk". */
  name: z.string().min(1).max(128),
  description: z.string().max(512).optional(),
  created_by: z.string().min(1),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
export type ProfileRecord = z.infer<typeof ProfileRecordSchema>;

/**
 * Per-field provenance, per environment (section 10.6). The
 * locally-modified marker - not the timestamp - decides whether a
 * promotion asks about a field: comparing timestamps alone loses a
 * production change whenever a later source change looks newer, which is
 * exactly when both sides were active.
 */
export const FieldMetaSchema = z.object({
  last_changed_at: z.string().datetime({ offset: true }),
  last_changed_by: z.string().min(1),
  /** Set when this environment edits the field directly; cleared when a promotion resolves it. */
  locally_modified: z.boolean(),
});
export type FieldMeta = z.infer<typeof FieldMetaSchema>;

/**
 * `ConfigurationVersions` - PK `${organisation_id}#${environment_id}#${profile_id}`,
 * SK `v` + zero-padded version. Immutable: publishing appends, and the
 * highest version is current, so rollback is a republish of an older
 * body rather than a mutation.
 */
export const ConfigurationVersionRecordSchema = z.object({
  pk: z.string().min(1),
  /** `v0000000012` - zero-padded so lexical order is numeric order. */
  sk: z.string().regex(/^v\d{10}$/),
  organisation_id: z.string().min(1),
  environment_id: z.string().min(1),
  profile_id: z.string().min(1),
  config_version: z.number().int().positive(),
  fields: z.array(ProfileFieldSchema),
  /** Keyed by `${type}/${name}`. */
  field_meta: z.record(FieldKeySchema, FieldMetaSchema),
  published_by: z.string().min(1),
  published_at: z.string().datetime({ offset: true }),
  /** Set where this version was produced by a promotion or a rollback rather than a direct edit. */
  source: z.enum(["direct", "promotion", "rollback"]),
  source_environment_id: z.string().optional(),
  rolled_back_from_version: z.number().int().positive().optional(),
});
export type ConfigurationVersionRecord = z.infer<typeof ConfigurationVersionRecordSchema>;

/**
 * Phone numbers are inventoried but are not a reference type: a profile
 * never points at one. The binding does, and the authoring UI needs the
 * real list to offer.
 */
export const InventoryTypeSchema = z.enum([...ReferenceFieldTypeSchema.options, "phone_number"]);
export type InventoryType = z.infer<typeof InventoryTypeSchema>;

/**
 * `Inventories` - PK `environment_id`, SK `${type}#${name}`. What this
 * environment's Connect instance actually has, pushed by the customer
 * account on a schedule. Configuration metadata, not CX content
 * (ADR-0006), so it is central and powers the authoring dropdowns.
 */
export const InventoryItemRecordSchema = z.object({
  environment_id: z.string().min(1),
  /** `${type}#${logical name}` */
  sk: z.string().min(1),
  type: InventoryTypeSchema,
  /** The human-readable name in the customer's instance, as they see it there. */
  name: z.string().min(1).max(256),
  arn: z.string().min(1),
  /** Connect's own id where it differs from the ARN tail (queues, flows). */
  resource_id: z.string().optional(),
  observed_at: z.string().datetime({ offset: true }),
});
export type InventoryItemRecord = z.infer<typeof InventoryItemRecordSchema>;

/** The `__state__` row on `Inventories` - ADR-0018 section 3, as central keeps it. SK is the literal `__state__`. */
export const InventoryStateRecordSchema = z.object({
  environment_id: z.string().min(1),
  sk: z.literal("__state__"),
  state: z.enum(["settled", "changing"]),
  settled_at: z.string().datetime({ offset: true }).optional(),
  last_change_at: z.string().datetime({ offset: true }).optional(),
  burst_calls: z.number().int().nonnegative().optional(),
  burst_principal: z.string().optional(),
  burst_first_call_at: z.string().datetime({ offset: true }).optional(),
  burst_last_call_at: z.string().datetime({ offset: true }).optional(),
  /** Central's own counter: moves on every push that replaced items, whatever template sent it. A promotion preview carries the version it was computed against. */
  version: z.number().int().nonnegative(),
  observed_at: z.string().datetime({ offset: true }),
});
export type InventoryStateRecord = z.infer<typeof InventoryStateRecordSchema>;

/**
 * `ReferenceAliases` - PK `environment_id`, SK `${type}#${logical name}`.
 *
 * Section 10.2: where the same name exists in every environment - the
 * normal case - a logical reference resolves automatically through each
 * environment's own inventory, and nothing needs configuring. This is the
 * manual override for where the names diverge: "Tier 1 Queue" in dev is
 * "Tier1Queue" in prod. Per environment, because the divergence is.
 */
export const ReferenceAliasRecordSchema = z.object({
  environment_id: z.string().min(1),
  /** `${type}#${logical name}` */
  sk: z.string().min(1),
  type: ReferenceFieldTypeSchema,
  /** The name the profile uses. */
  name: z.string().min(1).max(256),
  /** The name this environment's instance actually has. */
  resolves_to: z.string().min(1).max(256),
  set_by: z.string().min(1),
  set_at: z.string().datetime({ offset: true }),
});
export type ReferenceAliasRecord = z.infer<typeof ReferenceAliasRecordSchema>;

/** `Bindings` - PK `environment_id`, SK the E.164 number. One number reaches one profile. */
export const BindingRecordSchema = z.object({
  environment_id: z.string().min(1),
  phone_number: z.string().regex(/^\+[1-9]\d{6,14}$/),
  organisation_id: z.string().min(1),
  profile_id: z.string().min(1),
  /** Set where the customer bound this manually rather than by matching names across the chain. */
  manual: z.boolean(),
  bound_by: z.string().min(1),
  bound_at: z.string().datetime({ offset: true }),
});
export type BindingRecord = z.infer<typeof BindingRecordSchema>;

/**
 * `Overrides` - PK `${organisation_id}#${environment_id}`, SK
 * `${profile_id}#${type}/${name}`.
 *
 * A layer over the configuration, never an edit to it: never promoted,
 * never in a promotion diff, and it stops applying by itself. The window
 * is evaluated in the customer's account at call time, so an expiry
 * takes effect on schedule even if a sync is late (section 10.6).
 */
export const OverrideRecordSchema = z.object({
  pk: z.string().min(1),
  sk: z.string().min(1),
  organisation_id: z.string().min(1),
  environment_id: z.string().min(1),
  profile_id: z.string().min(1),
  field: FieldKeySchema,
  /** Same shape as the field it masks, minus the address. */
  value: z.unknown(),
  starts_at: z.string().datetime({ offset: true }),
  /** Absent means indefinite - allowed, and surfaced as ageing rather than expired. */
  expires_at: z.string().datetime({ offset: true }).optional(),
  reason: z.string().min(1).max(512),
  set_by: z.string().min(1),
  set_at: z.string().datetime({ offset: true }),
});
export type OverrideRecord = z.infer<typeof OverrideRecordSchema>;

export const PromotionStatusSchema = z.enum(["pending_approval", "applied", "rejected"]);
export type PromotionStatus = z.infer<typeof PromotionStatusSchema>;

/** What the admin chose for one contested field. Default is keep, so overwriting is opt-in. */
export const PromotionDecisionSchema = z.object({
  field: FieldKeySchema,
  choice: z.enum(["take_source", "keep_target"]),
});
export type PromotionDecision = z.infer<typeof PromotionDecisionSchema>;

/** `Promotions` - PK `organisation_id`, SK `${created_at}#${promotion_id}`. */
export const PromotionRecordSchema = z.object({
  organisation_id: z.string().min(1),
  sk: z.string().min(1),
  promotion_id: z.string().min(1),
  profile_id: z.string().min(1),
  /**
   * A gated direct edit rides this same record: ADR-0008 gates "any
   * change to that environment's configuration, whether a promotion into
   * it or a direct edit of it", and the approval path is identical, so
   * modelling them apart would duplicate it. `proposed_fields` carries
   * what a direct edit or rollback wants to publish; a promotion reads
   * its values from the source version instead.
   */
  kind: z.enum(["promotion", "direct_edit", "rollback"]),
  proposed_fields: z.array(ProfileFieldSchema).optional(),
  rollback_of_version: z.number().int().positive().optional(),
  source_environment_id: z.string().min(1),
  target_environment_id: z.string().min(1),
  source_version: z.number().int().positive(),
  /** The target version the diff was computed against; a later publish invalidates it. */
  target_version: z.number().int().nonnegative(),
  /** ADR-0018 section 4: the target's inventory version the preview was computed against; applying refuses if it has moved. */
  target_inventory_version: z.number().int().nonnegative().optional(),
  status: PromotionStatusSchema,
  decisions: z.array(PromotionDecisionSchema),
  requested_by: z.string().min(1),
  requested_at: z.string().datetime({ offset: true }),
  decided_by: z.string().optional(),
  decided_at: z.string().datetime({ offset: true }).optional(),
  /** ADR-0008: an admin pushing a gated change through where the customer enabled it. */
  break_glass: z.boolean().optional(),
  applied_version: z.number().int().positive().optional(),
});
export type PromotionRecord = z.infer<typeof PromotionRecordSchema>;

/**
 * What syncs into the customer's account: this environment's current
 * version resolved to real ARNs, plus its active overrides. Schedules
 * travel resolved-but-unevaluated because the lander evaluates them at
 * call time; override windows travel for the same reason.
 */
export const SyncedOverrideSchema = z.object({
  field: FieldKeySchema,
  value: z.unknown(),
  starts_at: z.string().datetime({ offset: true }),
  expires_at: z.string().datetime({ offset: true }).optional(),
});

export const ResolvedConfigurationSchema = z.object({
  environment_id: z.string().min(1),
  profile_id: z.string().min(1),
  profile_name: z.string().min(1),
  config_version: z.number().int().positive(),
  resolved_at: z.string().datetime({ offset: true }),
  /** Every authored field, references already resolved to ARNs. */
  fields: z.array(z.object({ field: FieldKeySchema, value: z.unknown() })),
  overrides: z.array(SyncedOverrideSchema),
});
export type ResolvedConfiguration = z.infer<typeof ResolvedConfigurationSchema>;

/** One sync payload covers every profile bound in the environment. */
export const ConfigurationSyncPayloadSchema = z.object({
  environment_id: z.string().min(1),
  generated_at: z.string().datetime({ offset: true }),
  /** Dialled number -> profile id, this environment's bindings. */
  bindings: z.record(z.string(), z.string()),
  profiles: z.array(ResolvedConfigurationSchema),
});
export type ConfigurationSyncPayload = z.infer<typeof ConfigurationSyncPayloadSchema>;

/**
 * The lander's wire format (section 10.3). Bulk is the default and the
 * flow should call it once at entry: Connect charges an invocation per
 * `Invoke AWS Lambda function` block and caps a chain at 20 s, so a flow
 * that calls out per value is what made the previous implementation
 * cumbersome. Targeted mode is for the genuinely late lookup.
 */
export const LanderRequestSchema = z.object({
  /** Connect sends the dialled number as the system endpoint. */
  dialled_number: z.string().min(1).optional(),
  profile: z.string().min(1).optional(),
  type: FieldTypeSchema.optional(),
  name: FieldNameSchema.optional(),
});
export type LanderRequest = z.infer<typeof LanderRequestSchema>;

/** Decisions, not raw data: the flow is told open/closed, never the schedule. */
export const HoursDecisionSchema = z.object({
  open: z.boolean(),
  reason: z.enum(["open", "closed", "holiday", "override"]),
  /** What to play, resolved: the named announcement's payload, else the text as authored. */
  announcement: z.string().optional(),
  /** The announcement's name when `announcement` came from one, so a flow can also fetch it by name. */
  announcement_name: z.string().optional(),
  /** Whether `announcement` is SSML (`<speak>…`), so a flow can pick the Play prompt block that interprets it. */
  announcement_ssml: z.boolean().optional(),
});
export type HoursDecision = z.infer<typeof HoursDecisionSchema>;

export const LanderResponseSchema = z.object({
  /** So a call can be traced to the exact configuration that drove it. */
  profile: z.string(),
  config_version: z.number().int().nonnegative(),
  resolved_at: z.string(),
  announcements: z.record(z.string(), z.string()).optional(),
  kv: z.record(z.string(), z.string()).optional(),
  voices: z.record(z.string(), z.string()).optional(),
  queues: z.record(z.string(), z.string()).optional(),
  flows: z.record(z.string(), z.string()).optional(),
  prompts: z.record(z.string(), z.string()).optional(),
  hours_of_operation: z.record(z.string(), z.string()).optional(),
  lex_bots: z.record(z.string(), z.string()).optional(),
  menus: z.record(z.string(), MenuSchema).optional(),
  hours: z.record(z.string(), HoursDecisionSchema).optional(),
  /** Targeted mode returns the one value here rather than a group. */
  value: z.unknown().optional(),
  /** Set when the lander answered from a safe default because it had nothing to read. */
  degraded: z.boolean().optional(),
});
export type LanderResponse = z.infer<typeof LanderResponseSchema>;

/** Wire group name per type: `announcement` -> `announcements`, so a flow reads `$.External.announcements.hold_1`. */
export const WIRE_GROUP: Record<FieldType, string> = {
  announcement: "announcements",
  kv: "kv",
  voice: "voices",
  hours: "hours",
  menu: "menus",
  queue: "queues",
  flow: "flows",
  prompt: "prompts",
  hours_of_operation: "hours_of_operation",
  lex_bot: "lex_bots",
};

/* -------------------------------------------------------------------------
 * Portal API contracts for the configuration module.
 * ---------------------------------------------------------------------- */

export interface ProfileSummary {
  profileId: string;
  name: string;
  description?: string;
  configVersion?: number;
  publishedAt?: string;
  publishedBy?: string;
}
export interface ProfilesResponse {
  profiles: ProfileSummary[];
  environmentId: string;
}

export interface OverrideView {
  profileId: string;
  field: string;
  value: unknown;
  startsAt: string;
  expiresAt?: string;
  reason: string;
  setBy: string;
  setAt: string;
}

/**
 * The four conditions section 10.6 keeps apart: two are facts, two are
 * faults. A publish the environment has not pulled yet is a fact
 * (`pendingSync`) until it has had a fair chance to; only then is it a
 * fault (`syncLag`).
 */
export interface ConditionsView {
  /** No number in this environment reaches the profile, so nothing is served or pulled - a fact, with one thing to do. */
  unbound?: true;
  versionGap?: { behind: number; sourceEnvironmentId: string };
  pendingSync?: { publishedVersion: number; publishedAt: string };
  syncLag?: { publishedVersion: number; syncedVersion?: number; publishedAt: string; syncedAt?: string };
  inventoryDrift?: Array<{ field: string; type: string; ref: string }>;
  staleOverrides: Array<{ field: string; expiresAt?: string; ageDays: number; setBy: string }>;
}

export interface ProfileDetailResponse {
  profile: { profileId: string; name: string; description?: string };
  environmentId: string;
  configVersion: number;
  fields: ProfileField[];
  fieldMeta: Record<string, FieldMeta>;
  publishedAt?: string;
  publishedBy?: string;
  overrides: OverrideView[];
  conditions: ConditionsView;
  /** ADR-0008: a customer with one environment has no promotion at all. */
  chain: string[];
  requiresApproval: boolean;
  breakGlassEnabled: boolean;
}

/** ADR-0018 section 4: what the API says about an instance's inventory - settled or mid-change, and at which version. */
export interface InventoryStateView {
  state: "settled" | "changing";
  version: number;
  settledAt?: string;
  lastChangeAt?: string;
  burst?: { calls: number; principal?: string; firstCallAt?: string; lastCallAt?: string };
}

export interface InventoryResponse {
  items: Array<{ type: InventoryType; name: string; arn: string; resourceId?: string; observedAt: string }>;
  observedAt?: string;
  /** Only present where a name diverges; the normal case needs none. */
  aliases: Array<{ type: ReferenceFieldType; name: string; resolvesTo: string; setBy: string; setAt: string }>;
  /** Absent until the environment's stack has pushed an inventory with ADR-0018's state. */
  state?: InventoryStateView;
}

export interface BindingsResponse {
  bindings: Array<{ phoneNumber: string; profileId: string; manual: boolean; boundBy: string; boundAt: string }>;
}

export interface VersionsResponse {
  versions: Array<{
    configVersion: number;
    publishedAt: string;
    publishedBy: string;
    source: "direct" | "promotion" | "rollback";
    sourceEnvironmentId?: string;
    rolledBackFromVersion?: number;
    fieldCount: number;
  }>;
}

export interface PromotionDiffResponse {
  uncontested: Array<{ field: string; before?: unknown; after: unknown }>;
  contested: Array<{
    field: string;
    sourceValue: unknown;
    targetValue: unknown;
    sourceChangedAt: string;
    targetChangedAt: string;
    recommendation: "keep_target" | "take_source";
  }>;
  masked: Array<{
    field: string;
    incomingValue: unknown;
    override: { value: unknown; setBy: string; setAt: string; expiresAt?: string; reason: string };
  }>;
  unchanged: number;
  sourceEnvironmentId: string;
  targetEnvironmentId: string;
  sourceVersion: number;
  targetVersion: number;
  /** Permitted, and flagged: it leaves the skipped environment behind. */
  skipsEnvironments: string[];
  requiresApproval: boolean;
  /** ADR-0018 section 4: the target's inventory as previewed; the apply carries its version and is refused if it moved. */
  targetInventory?: InventoryStateView;
}

export interface PromotionsResponse {
  promotions: Array<{
    promotionId: string;
    profileId: string;
    kind: "promotion" | "direct_edit" | "rollback";
    status: PromotionStatus;
    sourceEnvironmentId: string;
    requestedBy: string;
    requestedAt: string;
    decidedBy?: string;
    decidedAt?: string;
    appliedVersion?: number;
  }>;
}

export type PublishResult =
  | { status: "published"; configVersion: number }
  | { status: "pending_approval"; promotionId: string };

export type PromotionResult =
  | { status: "applied"; configVersion: number; applied: string[]; kept: string[] }
  | { status: "pending_approval"; promotionId: string; contested: number };
